/**
 * Alert history.
 *
 * Popups vanish and browser notifications are dismissed, so an alert that fired while nobody was
 * watching left no trace at all. Everything delivered is recorded here — newest first, surviving a
 * reload — with the conditions that matched and a way into the contract's history.
 */

import React, { useEffect, useState } from 'react';
import './AlertRulesPanel.css';
import {
  AlertEvent,
  clearAlertLog,
  loadAlertLog,
  onAlertLogChange,
} from '../../utils/alertRuntime';
import { useHistoricalModal } from '../../contexts/HistoricalModalContext';

function whenLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const sameDay =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  return sameDay ? d.toLocaleTimeString() : `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

export interface AlertLogProps {
  /** Show only this source's alerts; omit for everything. */
  sourceId?: string;
}

const AlertLog: React.FC<AlertLogProps> = ({ sourceId }) => {
  const [events, setEvents] = useState<AlertEvent[]>(() => loadAlertLog());
  const { openHistorical } = useHistoricalModal();

  useEffect(() => onAlertLogChange(setEvents), []);

  const shown = sourceId ? events.filter((e) => e.sourceId === sourceId) : events;

  if (!shown.length) {
    return (
      <div className="arHint">
        No alerts yet. Anything a rule fires is recorded here, whether or not you saw the popup.
      </div>
    );
  }

  return (
    <div className="alLog">
      <div className="alLogHead">
        <span className="arHint">
          {shown.length} alert{shown.length === 1 ? '' : 's'}, newest first
        </span>
        <button type="button" className="chip" onClick={clearAlertLog}>
          Clear
        </button>
      </div>
      {shown.map((e) => (
        <div
          className={`alRow${e.historical ? ' clickable' : ''}`}
          key={e.id}
          role={e.historical ? 'button' : undefined}
          tabIndex={e.historical ? 0 : undefined}
          title={e.historical ? 'Open contract history' : undefined}
          onClick={() => e.historical && openHistorical({ ...e.historical, source: 'volume' })}
          onKeyDown={(ev) => {
            if (e.historical && (ev.key === 'Enter' || ev.key === ' ')) {
              ev.preventDefault();
              openHistorical({ ...e.historical, source: 'volume' });
            }
          }}
        >
          <div className="alWhen mono">{whenLabel(e.at)}</div>
          <div className="alBody">
            <div className="alRule">
              {e.ruleName}
              {e.sourceLabel ? <span className="src"> · {e.sourceLabel}</span> : null}
            </div>
            <div className="alSummary">{e.summary}</div>
            {e.details?.length ? (
              <div className="alWhy mono">{e.details.join('  ·  ')}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlertLog;
